import { useEffect, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';
import { appeler } from '../../api/client.ts';
import { useSession } from '../../composants/session.tsx';
import { Entete } from '../../composants/mise-en-page.tsx';
import { Icone } from '../../composants/icones.tsx';
import {
  Avertissements,
  Carte,
  Chargement,
  Erreur,
  Modale,
} from '../../composants/communs.tsx';
import { FormulaireModifierProduit } from './FormulaireModifierProduit.tsx';
import { FormulairePrix } from './FormulairePrix.tsx';
import { FormulaireSeuil } from './FormulaireSeuil.tsx';
import { ListeProduits } from './liste.tsx';
import { Photos } from './Photos.tsx';
import { MouvementsProduit } from '../stock/MouvementsProduit.tsx';
import { FicheDistribution } from './FicheDistribution.tsx';
import {
  CANAUX,
  LIBELLE_CANAL,
  versProduit,
  type FiltreCanal,
  type Produit,
  type ProduitGroupe,
  type Referentiel,
} from './types.ts';

/** Lignes par page à l'écran. */
const LIGNES_PAR_PAGE = 50;
/** Lignes par requête quand on lit TOUT, pour l'impression : moins d'allers-retours. */
const LIGNES_PAR_REQUETE_IMPRESSION = 200;

interface PageProduits {
  donnees: ProduitGroupe[];
  curseur_suivant: string | null;
}

/**
 * Catalogue — RG-019 à RG-038.
 *
 * La liste montre TOUS les canaux par défaut, une ligne par produit et par
 * canal ; le select restreint ensuite à un seul canal. Elle est paginée par
 * curseur (50 par page), et l'impression, elle, lit TOUTE la liste filtrée.
 * Un produit sans prix actif sur un canal n'y est pas vendable : c'est
 * l'information la plus utile de l'écran.
 */
export function Produits(): ReactNode {
  const { boutiqueId, boutiques, peut } = useSession();
  // Boutique dont on lit l'historique : celle du contexte, sinon l'unique
  // boutique du compte. Sans l'une ni l'autre, pas d'historique possible.
  const boutiqueHistorique =
    boutiques.find((b) => b.id === boutiqueId) ?? (boutiques.length === 1 ? boutiques[0] : undefined);
  const client = useQueryClient();
  const localisation = useLocation();

  const [canal, setCanal] = useState<FiltreCanal>('TOUS');
  const [recherche, setRecherche] = useState('');
  const [categorieId, setCategorieId] = useState('');
  // Ignoré par le serveur pour qui n'a pas `produit.modifier` — inutile de
  // proposer la case à un vendeur qui n'en tirerait rien.
  const [sansPrix, setSansPrix] = useState(true);
  // Reçus de `/produits/nouveau` à la navigation retour — voir NouveauProduit.
  // Lus une seule fois au montage : cette page se démonte en quittant vers
  // `/produits/nouveau`, donc `useState` les capture à chaque retour.
  const [avertissements] = useState<{ code: string; message: string }[]>(
    (localisation.state as { avertissements?: { code: string; message: string }[] } | null)
      ?.avertissements ?? [],
  );
  const [produitModifie, setProduitModifie] = useState<Produit | null>(null);
  const [produitTarife, setProduitTarife] = useState<Produit | null>(null);
  /** Produit à plusieurs canaux dont on doit d'abord choisir le canal à tarifer. */
  const [produitACanal, setProduitACanal] = useState<ProduitGroupe | null>(null);
  const [produitSeuil, setProduitSeuil] = useState<Produit | null>(null);
  const [produitIllustre, setProduitIllustre] = useState<Produit | null>(null);
  /** Produit dont on regle la distribution par boutique — RG-196. */
  const [produitDistribue, setProduitDistribue] = useState<Produit | null>(null);
  /** Produit dont on consulte le détail et l'historique de mouvements. */
  const [produitVu, setProduitVu] = useState<Produit | null>(null);

  // Pile des curseurs des pages déjà parcourues (même schéma que le Journal).
  // Elle est rattachée aux filtres qui l'ont produite : dès qu'un filtre
  // change, elle n'est plus lue et on repart de la première page, sans
  // jamais envoyer un curseur issu d'un autre filtre.
  const cleFiltres = [boutiqueId ?? '', canal, recherche, categorieId, sansPrix].join('|');
  const [pile, setPile] = useState<{ cle: string; curseurs: string[] }>({
    cle: '',
    curseurs: [],
  });
  const curseurs = pile.cle === cleFiltres ? pile.curseurs : [];
  const curseur = curseurs.at(-1);

  /** Paramètres communs à la page affichée et à l'impression complète. */
  function parametres(): string {
    return (
      (canal === 'TOUS' ? '' : `&canal=${canal}`) +
      (boutiqueId === null ? '' : `&boutique_id=${boutiqueId}`) +
      (recherche.trim() === '' ? '' : `&q=${encodeURIComponent(recherche.trim())}`) +
      (categorieId === '' ? '' : `&categorie_id=${categorieId}`) +
      (sansPrix ? '&inclure_sans_prix=1' : '')
    );
  }

  // La clé commence par 'produits' : les écrans qui invalident ['produits']
  // après un changement de prix ou de seuil rafraîchissent aussi cette liste.
  const { data, isPending, error } = useQuery({
    queryKey: ['produits', 'liste', cleFiltres, curseur],
    queryFn: () =>
      appeler<PageProduits>(
        `/catalogue/produits?groupe=1&limite=${String(LIGNES_PAR_PAGE)}${parametres()}` +
          (curseur === undefined ? '' : `&curseur=${curseur}`),
      ),
  });

  /**
   * Toute la liste filtrée, page après page — pour l'impression.
   *
   * Indépendante de la page affichée : s'il y a 150 lignes et que l'écran en
   * montre 50, le PDF en contient 150.
   */
  async function chargerTout(): Promise<ProduitGroupe[]> {
    const tous: ProduitGroupe[] = [];
    let suivant: string | null = null;
    // Garde-fou : 50 requêtes de 200 lignes, soit 10 000 lignes.
    for (let tour = 0; tour < 50; tour++) {
      const page: PageProduits = await appeler<PageProduits>(
        `/catalogue/produits?groupe=1&limite=${String(LIGNES_PAR_REQUETE_IMPRESSION)}${parametres()}` +
          (suivant === null ? '' : `&curseur=${suivant}`),
      );
      tous.push(...page.donnees);
      suivant = page.curseur_suivant;
      if (suivant === null) break;
    }
    return tous;
  }

  // Options du filtre catégorie — même référentiel que le formulaire de
  // création, React Query dédoublonne l'appel entre les deux écrans.
  const { data: referentiel } = useQuery({
    queryKey: ['referentiel-catalogue'],
    queryFn: () => appeler<Referentiel>('/referentiel/catalogue'),
  });

  function rafraichir(): void {
    void client.invalidateQueries({ queryKey: ['produits'] });
  }

  // Retour de `/produits/nouveau` : le produit créé n'est pas dans le cache.
  useEffect(() => {
    if (localisation.state !== null) {
      rafraichir();
    }
  }, [localisation.state]);

  const produits = data?.donnees ?? [];
  const avecCouts = produits.some((produit) => produit.coutMoyen !== undefined);

  return (
    <>
      <Entete
        titre="Produits et prix"
        sous={
          boutiqueId === null
            ? 'Prix de référence et stock cumulé sur toutes vos boutiques. Filtrez sur une boutique pour voir sa surcharge de prix.'
            : 'Un produit sans prix actif sur un canal n’y est pas vendable.'
        }
        actions={
          peut('produit.creer') ? (
            <Link to="/produits/nouveau" className="bouton">
              Nouveau produit
            </Link>
          ) : undefined
        }
      />

      <div className="pile">
        <Avertissements
          liste={avertissements.length === 0 ? undefined : avertissements}
        />

        {produitModifie !== null && (
          <Modale onFermer={() => { setProduitModifie(null); }}>
            <FormulaireModifierProduit
              produit={produitModifie}
              onFerme={() => {
                setProduitModifie(null);
              }}
              onEnregistre={rafraichir}
            />
          </Modale>
        )}

        {produitTarife !== null && (
          <Modale onFermer={() => { setProduitTarife(null); }}>
            <FormulairePrix
              produit={produitTarife}
              boutiqueId={boutiqueId}
              onFerme={() => {
                setProduitTarife(null);
              }}
              onEnregistre={rafraichir}
            />
          </Modale>
        )}

        {produitACanal !== null && (
          <Modale
            onFermer={() => {
              setProduitACanal(null);
            }}
          >
            <Carte titre={`Prix de ${produitACanal.designation}`}>
              <p>Pour quel canal voulez-vous définir le prix ?</p>
              <div className="ligne">
                {produitACanal.canaux.map((entree) => (
                  <button
                    key={entree.canal}
                    type="button"
                    className="bouton--discret"
                    onClick={() => {
                      setProduitTarife(versProduit(produitACanal, entree));
                      setProduitACanal(null);
                    }}
                  >
                    {LIBELLE_CANAL[entree.canal]}
                    {entree.prixUnitaire === null ? ' · sans prix' : ''}
                  </button>
                ))}
              </div>
            </Carte>
          </Modale>
        )}

        {produitSeuil !== null && (
          <Modale onFermer={() => { setProduitSeuil(null); }}>
            <FormulaireSeuil
              produit={produitSeuil}
              boutiqueId={boutiqueId}
              onFerme={() => {
                setProduitSeuil(null);
              }}
              onEnregistre={rafraichir}
            />
          </Modale>
        )}

        {produitDistribue !== null && (
          <Modale onFermer={() => { setProduitDistribue(null); }}>
            <FicheDistribution
              produitId={produitDistribue.id}
              designation={produitDistribue.designation}
              onFerme={() => {
                setProduitDistribue(null);
              }}
            />
          </Modale>
        )}

        {produitVu !== null && (
          <Modale onFermer={() => { setProduitVu(null); }}>
            {boutiqueHistorique === undefined ? (
              <Carte titre={produitVu.designation}>
                <p>Choisissez une boutique en haut de page pour voir l’historique des mouvements.</p>
              </Carte>
            ) : (
              <MouvementsProduit
                ligne={{
                  produit_id: produitVu.id,
                  reference: produitVu.reference,
                  designation: produitVu.designation,
                  boutique_id: boutiqueHistorique.id,
                  boutique: boutiqueHistorique.nom,
                  quantite: produitVu.stockDisponible,
                  seuil: null,
                  sous_seuil: false,
                }}
                onFerme={() => {
                  setProduitVu(null);
                }}
              />
            )}
          </Modale>
        )}

        {produitIllustre !== null && (
          <Modale onFermer={() => { setProduitIllustre(null); }}>
            <Photos
              produit={produitIllustre}
              modifiable={peut('produit.photo.gerer')}
              onFerme={() => {
                setProduitIllustre(null);
              }}
            />
          </Modale>
        )}

        <Carte>
          <div className="ligne" style={{ marginBottom: 12 }}>
            <div className="champ" style={{ margin: 0, minWidth: 160 }}>
              <label htmlFor="canal">Canal</label>
              <select
                id="canal"
                value={canal}
                onChange={(evenement) => {
                  setCanal(evenement.target.value as FiltreCanal);
                }}
              >
                <option value="TOUS">Tous les canaux</option>
                {CANAUX.map((valeur) => (
                  <option key={valeur} value={valeur}>
                    {LIBELLE_CANAL[valeur]}
                  </option>
                ))}
              </select>
            </div>
            <div className="champ" style={{ margin: 0, minWidth: 180 }}>
              <label htmlFor="categorie-filtre">Catégorie</label>
              <select
                id="categorie-filtre"
                value={categorieId}
                onChange={(evenement) => {
                  setCategorieId(evenement.target.value);
                }}
              >
                <option value="">Toutes</option>
                {(referentiel?.categories ?? []).map((categorie) => (
                  <option key={categorie.id} value={categorie.id}>
                    {categorie.libelle}
                  </option>
                ))}
              </select>
            </div>
            <div className="champ" style={{ margin: 0, flex: 1, minWidth: 200 }}>
              <label htmlFor="recherche">Référence ou désignation</label>
              <input
                id="recherche"
                value={recherche}
                placeholder="Rechercher…"
                onChange={(evenement) => {
                  setRecherche(evenement.target.value);
                }}
              />
            </div>
            {peut('produit.modifier') && (
              <div className="champ" style={{ margin: 0 }}>
                <label htmlFor="sans-prix" style={{ fontWeight: 400 }}>
                  <input
                    id="sans-prix"
                    type="checkbox"
                    style={{ width: 'auto', marginRight: 6 }}
                    checked={sansPrix}
                    onChange={(evenement) => {
                      setSansPrix(evenement.target.checked);
                    }}
                  />
                  Voir aussi les produits sans prix
                </label>
              </div>
            )}
          </div>

          <Erreur erreur={error} />

          {isPending ? (
            <Chargement />
          ) : (
            <ListeProduits
              produits={produits}
              canal={canal}
              chargerTout={chargerTout}
              avecCouts={avecCouts}
              actions={(produit) => (
                <>
                  <button
                    type="button"
                    className="bouton--discret bouton--icone"
                    title="Détail et mouvements"
                    aria-label={`Détail et mouvements de ${produit.designation}`}
                    onClick={() => {
                      setProduitVu(versProduit(produit));
                    }}
                  >
                    <Icone nom="oeil" />
                  </button>
                  {peut('produit.modifier') && (
                    <button
                      type="button"
                      className="bouton--discret bouton--icone bouton--icone-modifier"
                      title="Modifier"
                      aria-label={`Modifier ${produit.designation}`}
                      onClick={() => {
                        setProduitModifie(versProduit(produit));
                      }}
                    >
                      <Icone nom="crayon" />
                    </button>
                  )}
                  {peut('prix.creer') && (
                    <button
                      type="button"
                      className="bouton--discret bouton--icone bouton--icone-prix"
                      title="Prix"
                      aria-label={`Prix de ${produit.designation}`}
                      onClick={() => {
                        // Un prix se fixe PAR canal : s'il y en a plusieurs, on
                        // demande lequel avant d'ouvrir le formulaire.
                        if (produit.canaux.length > 1) {
                          setProduitACanal(produit);
                        } else {
                          setProduitTarife(versProduit(produit));
                        }
                      }}
                    >
                      <Icone nom="monnaie" />
                    </button>
                  )}
                  {peut('produit.modifier') && (
                    <button
                      type="button"
                      className="bouton--discret bouton--icone bouton--icone-seuil"
                      title="Seuil"
                      aria-label={`Seuil de réapprovisionnement de ${produit.designation}`}
                      onClick={() => {
                        setProduitSeuil(versProduit(produit));
                      }}
                    >
                      <Icone nom="alerte" />
                    </button>
                  )}
                  {peut('produit.modifier') && (
                  <button
                    type="button"
                    className="bouton--discret bouton--icone bouton--icone-boutiques"
                    title="Boutiques"
                    aria-label={`Boutiques de ${produit.designation}`}
                    onClick={() => {
                      setProduitDistribue(versProduit(produit));
                    }}
                  >
                    <Icone nom="boutiques" />
                  </button>
                  )}
                  {peut('produit.photo.gerer') && (
                  <button
                    type="button"
                    className="bouton--discret bouton--icone bouton--icone-photos"
                    title="Photos"
                    aria-label={`Photos de ${produit.designation}`}
                    onClick={() => {
                      setProduitIllustre(versProduit(produit));
                    }}
                  >
                    <Icone nom="image" />
                  </button>
                  )}
                </>
              )}
            />
          )}

          {data !== undefined && (curseurs.length > 0 || data.curseur_suivant !== null) && (
            <div className="ligne" style={{ marginTop: 14, alignItems: 'center' }}>
              <button
                type="button"
                className="bouton--discret"
                disabled={curseurs.length === 0}
                onClick={() => {
                  setPile({ cle: cleFiltres, curseurs: curseurs.slice(0, -1) });
                }}
              >
                Précédent
              </button>
              <span className="entete__sous">Page {curseurs.length + 1}</span>
              <button
                type="button"
                className="bouton--discret"
                disabled={data.curseur_suivant === null}
                onClick={() => {
                  const suivant = data.curseur_suivant;
                  if (suivant !== null) {
                    setPile({ cle: cleFiltres, curseurs: [...curseurs, suivant] });
                  }
                }}
              >
                Suivant
              </button>
            </div>
          )}
        </Carte>
      </div>
    </>
  );
}
